import { Collapse as MantineCollapse } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useTheme } from 'styled-components';

import { Icon, Text } from '../../ui';
import {
  StyledContent,
  StyledContentWrapper,
  StyledIconWrapper,
  StyledLabel,
  StyledLabelExtraWrapper,
  StyledLabelTextWrapper,
  StyledWrapper,
} from './Collapse.style';

export interface CollapseProps {
  label?: {
    icon?: string | React.ReactNode;
    text: string | React.ReactNode;
    extra?: string | React.ReactNode;
  };
  isOpened?: boolean;
  variant?: 'outline' | 'default' | 'simple' | 'ghost';
  children: React.ReactNode;
  onChange?: (isOpened: boolean) => void;
}

export function Collapse({
  label,
  children,
  isOpened: propIsOpened = false,
  variant = 'default',
  onChange,
}: CollapseProps): JSX.Element {
  const theme = useTheme();
  const [isOpened, setIsOpened] = useState(propIsOpened);

  const handleOnChange = (opened: boolean) => {
    setIsOpened(opened);
    onChange?.(opened);
  };

  useEffect(() => {
    handleOnChange(propIsOpened);
  }, [propIsOpened]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <StyledWrapper variant={variant} isOpened={isOpened}>
      {label && (
        <StyledLabel
          variant={variant}
          isOpened={isOpened}
          onClick={() => handleOnChange(!isOpened)}
        >
          {label.icon && (
            <StyledIconWrapper>
              {typeof label.icon === 'string' ? (
                <Icon name={label.icon} color={theme.Color.Neutrals.Text} size={'XSmall'} />
              ) : (
                label.icon
              )}
            </StyledIconWrapper>
          )}

          <StyledLabelTextWrapper>
            {typeof label.text === 'string' ? (
              <Text size={'SmallHeavy'} color={theme.Color.Neutrals.Dark}>
                {label.text}
              </Text>
            ) : (
              label.text
            )}
          </StyledLabelTextWrapper>

          {label.extra && (
            <StyledLabelExtraWrapper>
              {typeof label.extra === 'string' ? (
                <Text size={'SmallHeavy'} color={theme.Color.Neutrals.Text}>
                  {label.extra}
                </Text>
              ) : (
                label.extra
              )}
            </StyledLabelExtraWrapper>
          )}
        </StyledLabel>
      )}

      <StyledContentWrapper>
        <MantineCollapse in={isOpened} transitionTimingFunction="linear">
          <StyledContent variant={variant}>{children}</StyledContent>
        </MantineCollapse>
      </StyledContentWrapper>
    </StyledWrapper>
  );
}
